import React, { Component, memo } from 'react';
import { debounce, get, size, assign, isFunction, head, isNil } from 'lodash';
import ReactTable from 'react-table';
import { CheckboxCell } from '../../../screens/ChargersList/components/ChargersTableComponents';

import TableMultiselect, { handleMultiselect } from '../../../screens/ChargersList/components/TableMultiselect/TableMultiselect';
import { dashboardTableId } from '../../../screens/Dashboard/component';
import { multiFwUpdateTableId } from '../../../screens/MultiFwUpdate/constants';
import { fwVersionsChartChargersTableID } from '../../../screens/Reports/constants';
import { configUpdateTableId } from '../../../screens/ConfigUpdate/constants';

import ListPagination from '../Form/ListPagination';
import Loader from '../Loader/Loader';
import { getCheckboxColumn, getColumnClasses } from '../CommonTableColumns';

export const chargersMultiselectFilter = [
  multiFwUpdateTableId,
  fwVersionsChartChargersTableID,
  'chargersList',
  dashboardTableId,
  configUpdateTableId,
];

const loader = (
  <div className="loader-container">
    <Loader />
  </div>
);

class SSPList extends Component {
  debouncedGetData = debounce(this.getData, 500);

  constructor(props) {
    super(props);
    this.state = {
      isFilterApplying: false,
    };
    this.onPageChange = this.onPageChange.bind(this);
    this.onPageSizeChange = this.onPageSizeChange.bind(this);
    this.onFilteredChange = this.onFilteredChange.bind(this);
    this.onSortedChange = this.onSortedChange.bind(this);
    this.scrollTop = this.scrollTop.bind(this);
    this.tableComponent = null;

    this.clearFilter();
    const storedPageSize = get(this.props, 'data.page_size') || this.props.defaultPageSize;

    this.props.init({ page_size: storedPageSize });
    this.polling();
  }

  polling() {
    const { polling } = this.props;

    if (polling && polling.interval) {
      this.intervalUpdate = setInterval(() => {
        this.getData(
          this.props.id,
          {
            page_token: this.props.data.page_token,
            page_size: this.props.data.page_size,
            filter_by: this.props.data.filter,
            sorting: this.props.data.sorting,
            dynamicSectionName: this.props.data.dynamicSectionName,
            filterQuery: this.props.data.appliedQuery,
            polling: true,
          },
          { scrollToTop: false },
        );
      }, polling.interval);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { forceUpdate, page_token: pageToken, page_size: pageSize, sorting, filter, appliedQuery } = nextProps.data;

    if (forceUpdate) {
      this.getData(nextProps.id, {
        page_token: pageToken,
        page_size: pageSize,
        sorting,
        filter_by: filter,
        dynamicSectionName: nextProps.dynamicSectionName,
        filterQuery: appliedQuery,
      });
      const selectedDataIdsSize = size(get(this.props, 'selectedDataIds', []));

      if (this.props.selectAll || selectedDataIdsSize > 0) {
        this.props.deselectAllRows();
      }
    }

    if (this.props.data.appliedQuery !== appliedQuery) {
      this.scrollTop();
    }
  }

  componentWillUnmount() {
    this.clearFilter();
    this.props.deselectAllRows();
    clearInterval(this.intervalUpdate);
  }

  clearFilter() {
    const { clearFilterOnInit, defaultSorted } = this.props;

    if (clearFilterOnInit) {
      this.props.onFilterChange({ filter_by: null });
      this.props.onSortChange({ sorting: defaultSorted });
    }
  }

  getData(id, opts, { scrollToTop = true } = {}) {
    if (isFunction(this.props.onGetDataAction) && !this.props.isFetching) {
      this.props.onGetDataAction(id, opts);
      if (scrollToTop) this.scrollTop();
    }
  }

  scrollTop() {
    const table = document.querySelector('.rt-table');

    if (table) {
      table.scrollTop = 0;
    }
  }

  onPageChange(pageToken) {
    this.props.onPageSizeChange({ page_token: pageToken, page_size: this.props.data.page_size });

    clearInterval(this.intervalUpdate);

    this.getData(this.props.id, {
      page_token: pageToken,
      page_size: this.props.data.page_size,
      filter_by: this.props.data.filter,
      sorting: this.props.data.sorting,
      dynamicSectionName: this.props.dynamicSectionName,
      filterQuery: this.props.data.appliedQuery,
    });

    this.polling();
  }

  onPageSizeChange(pageSize, pageToken) {
    this.props.onPageSizeChange({ page_token: pageToken, page_size: pageSize });
    this.getData(this.props.id, {
      page_token: pageToken,
      page_size: pageSize,
      filter_by: this.props.data.filter,
      sorting: this.props.data.sorting,
      dynamicSectionName: this.props.dynamicSectionName,
      filterQuery: this.props.data.appliedQuery,
    });
  }

  onFilteredChange(filter, column) {
    this.setState({ isFilterApplying: true });
    let filterValid = filter.filter((item) => size(item) > 0);

    if (column.isDynamicField) {
      filterValid = filterValid.map((item) =>
        column.id === item.id
          ? assign({}, item, {
              isDynamicField: true,
            })
          : item,
      );
    }
    this.props.onFilterChange({ filter_by: filterValid });

    this.debouncedGetData(this.props.id, {
      page_token: 0,
      page_size: this.props.data.page_size,
      filter_by: filterValid,
      sorting: this.props.data.sorting,
      dynamicSectionName: this.props.dynamicSectionName,
      filterQuery: this.props.data.appliedQuery,
    });
  }

  onSortedChange(fields) {
    const sorting = head(fields);

    if (sorting) {
      this.props.onSortChange({ sorting });
      this.getData(this.props.id, {
        page_token: 0,
        page_size: this.props.data.page_size,
        filter_by: this.props.data.filter,
        sorting,
        dynamicSectionName: this.props.dynamicSectionName,
        filterQuery: this.props.data.appliedQuery,
      });
    }
  }

  isFilterApplied = () =>
    this.state.isFilterApplying || get(this.props, 'data.filterQuery.rules', []).length > 0 || get(this.props, 'data.filter');

  renderListPagination = (props) => (
    <ListPagination
      selectAll={this.props.selectAll}
      selectedDataIds={this.props.selectedDataIds}
      exportable={this.props.exportable}
      onExportTable={this.props.exportTable}
      exportLimit={this.props.exportLimit}
      renderOptButton={this.props.optButton}
      isExporting={this.props.isExporting}
      confirm={this.props.confirm}
      {...props}
    />
  );

  renderNoDataComponent = () => {
    if (this.props.error) {
      return <div className="rt-noData">{this.props.error}</div>;
    }

    return <div className="rt-noData">No rows found</div>;
  };

  render() {
    const {
      data: { data, isFetching, page_size: pageSize, page_token: pageToken, totalCount, sorting, filter },
      columns,
      filterable,
      noSSP,
      defaultSorted,
      mobileSticky,
      isModal,
      optPaginationComponent,
      optStyles = null,
      messageEmptyList,
      isShowMessageEmptyList,
      isFirstFetching,
      checkboxSticky,
      rowsFreeHeight,
    } = this.props;

    const pages = Math.ceil(totalCount / pageSize);
    const filterableFlag = isNil(filterable) ? true : filterable;

    const sortingObj = [size(sorting) ? sorting : {}];
    const filterObj = size(filter) ? filter : [];

    const filterClass = filterableFlag ? '' : 'non-filterable';
    const modalClass = mobileSticky ? (isModal ? 'sticky sticky-modal' : 'sticky') : '';

    let configuredColumns = columns;

    const handleChargersMultiselect = (selectionMode) =>
      handleMultiselect({
        selectionMode,
        setAllSelected: this.props.setAllSelected,
        setCurrentPageSelected: this.props.setCurrentPageSelected,
        deselectAllRows: this.props.deselectAllRows,
      });

    const getCellId = (original) => (chargersMultiselectFilter.includes(this.props.id) ? original.c_uuid : original.uuid);

    if (this.props.multiselect && !noSSP) {
      configuredColumns = [
        {
          ...getCheckboxColumn({ isSticky: checkboxSticky }),
          Header: () => (
            <TableMultiselect
              onSelect={handleChargersMultiselect}
              selectedCount={this.props.selectAll ? this.props.totalCount : size(this.props.selectedDataIds)}
            />
          ),
          Cell: ({ original }) => (
            <CheckboxCell
              id={getCellId(original)}
              activeChargerIds={this.props.selectedDataIds}
              onRowSelected={this.props.selectRow}
              selectAll={this.props.selectAll}
            />
          ),
        },
        ...columns,
      ].map(column => ({
        ...column,
        ...getColumnClasses({
          customCellClassName: column.className,
          isFreeHeight: rowsFreeHeight
        })
      }))
    }

    const showEmptyListMessage = totalCount === 0 && !this.isFilterApplied() && !isFetching && isShowMessageEmptyList;

    const sspList = showEmptyListMessage ? (
      <div className="no-chargers-label">{messageEmptyList}</div>
    ) : (
      <ReactTable
        style={optStyles}
        className={`${modalClass} ${filterClass}`}
        sorted={sortingObj}
        ref={(ref) => {
          this.tableComponent = ref;
        }}
        filtered={filterObj}
        loading={isFetching || false}
        data={data || []}
        page={pageToken || 0}
        pages={pages || 0}
        pageSize={pageSize || 0}
        totalCount={totalCount}
        manual={!noSSP}
        columns={configuredColumns}
        filterable={filterableFlag}
        PaginationComponent={optPaginationComponent || this.renderListPagination}
        onPageChange={this.onPageChange}
        onPageSizeChange={this.onPageSizeChange}
        onFilteredChange={this.onFilteredChange}
        onSortedChange={this.onSortedChange}
        NoDataComponent={this.renderNoDataComponent}
      />
    );
    // TODO: need to remove noSSP in future
    //  old stuff for failures, sessions, until api will be implemented for them
    const table = noSSP ? (
      <ReactTable
        defaultSorted={[defaultSorted]}
        data={data || []}
        loading={isFetching || false}
        columns={configuredColumns}
        filterable={filterableFlag}
        totalCount={totalCount}
        PaginationComponent={ListPagination}
        onPageChange={this.scrollTop}
        ref={(ref) => {
          this.tableComponent = ref;
        }}
      />
    ) : // old stuff end

    // normal SSPList stuff for server side pagination
    isFirstFetching ? (
      loader
    ) : (
      sspList
    );

    return table;
  }
}

export default memo(SSPList);
